import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import type { AppRegistryCallbackContext } from '@opengov/psp-contract';
import { useEntity } from '../../contexts/EntityContext';
import { useAuth } from '../../hooks/useAuth';
import { useModuleLoader } from '../../hooks/useModuleLoader';
import {
  useShellBridgeMount,
  type Auth0Ref,
  type PreBridgeMountArgs,
} from '../../hooks/useShellBridgeMount';
import { auth0Config } from '../../utils';
import { detectPspHostingEnvironment } from '../../services/utils';
import type { UnresolvedGlobalRegistryEntry, RemoteModuleConfig } from '../../types';

interface Props {
  entry: UnresolvedGlobalRegistryEntry;
}

export function GlobalAppShell({ entry }: Props) {
  const hostingEnvironment = detectPspHostingEnvironment();
  const { activeEntity } = useEntity();
  const {
    token,
    isAuthenticated,
    isLoading,
    user,
    error: authError,
    getAccessTokenSilently,
    getIdTokenClaims,
    logout,
  } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const showDebugInfoRef = useRef<boolean | undefined>(false);

  // Resolve URL once — env-based resolvers are synchronous and hostingEnvironment never changes
  const [resolvedUrl] = useState<string | null>(() => {
    const ctx: AppRegistryCallbackContext = {
      hostingEnvironment,
      auth0Config,
      activeEntity: undefined,
    };
    const result = entry.moduleUrl(ctx);
    return typeof result === 'string' && result ? result : null;
  });
  const resolvedUrlRef = useRef(resolvedUrl);

  // Resolve once — moduleConfig is synchronous-only for global apps
  const [resolvedModuleConfig] = useState<Record<string, unknown>>(() => {
    if (!entry.moduleConfig) return {};
    const ctx: AppRegistryCallbackContext = {
      hostingEnvironment,
      auth0Config,
      activeEntity: undefined,
    };
    const result = entry.moduleConfig(ctx);
    if (result instanceof Promise) {
      console.warn(`[GlobalAppShell] Async moduleConfig is not supported for ${entry.id}`);
      return {};
    }
    return result;
  });

  const appConfig: RemoteModuleConfig = {
    id: entry.id,
    basePath: '/',
    moduleRoutes: [],
  };

  // mountArgsRef and auth0Ref are updated every render so the bridge always reads
  // fresh auth/entity values without triggering a remount
  const mountArgsRef = useRef<PreBridgeMountArgs | null>(null);
  mountArgsRef.current = {
    baseRouterPath: '/',
    auth0Config: token ? { ...auth0Config, token } : auth0Config,
    activeEntity: activeEntity ?? undefined,
    hostingEnvironment,
    registryConfig: resolvedModuleConfig,
  };

  const auth0Ref = useRef<Auth0Ref | null>(null);
  auth0Ref.current = {
    isAuthenticated,
    isLoading,
    user,
    error: authError,
    getAccessTokenSilently,
    getIdTokenClaims,
    logout,
  };

  const { module } = useModuleLoader(resolvedUrl);

  // mountError observability lives inside useShellBridgeMount (Faro via
  // useLoggerWithContext), so global apps need only consume the return shape
  // for TS satisfaction. Visible UI for global apps is intentionally out of
  // scope — the container is zero-size and renders via portals.
  void useShellBridgeMount({
    module,
    containerRef,
    mountArgsRef,
    auth0Ref,
    remountDeps: [],
    appConfig,
    hostingEnvironment,
    showDebugInfoRef,
    resolvedModuleUrlRef: resolvedUrlRef,
  });

  // Zero-size container: the global app renders its visible UI via portals
  // (e.g. requestHeaderSlot(), fixed-position drawers). aria-hidden keeps
  // screen readers from announcing the empty container.
  return (
    <Box
      ref={containerRef}
      sx={{ position: 'absolute', width: 0, height: 0, overflow: 'visible', pointerEvents: 'none' }}
      aria-hidden="true"
    />
  );
}
